/**
 * GuidedTourSandbox
 *
 * Module-aware sandbox banner that replaces DemoModeFloatingBanner.
 * Renders only when a demo/sandbox session is active for the current
 * module route and provides an exit button.
 */

'use client';

import React from 'react';
import { useTourSandbox } from './hooks/useTourSandbox';

export interface GuidedTourSandboxProps {
  moduleKey?: string;
  moduleColor?: string;
  autoActivate?: boolean;
  className?: string;
}

const formatSandboxLabel = (moduleKey: string | null): string => {
  if (!moduleKey) return 'Sandbox';
  return moduleKey
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

export function GuidedTourSandbox({
  moduleKey,
  moduleColor,
  autoActivate = false,
  className = '',
}: GuidedTourSandboxProps) {
  const { demoSession, isSandboxActive, loading, error, deactivate } = useTourSandbox({
    moduleKey,
    autoActivate,
  });

  if (!isSandboxActive) {
    return null;
  }

  const borderColor = moduleColor
    || (demoSession?.module_key === 'total-finance' ? '#0091c2' : undefined);

  return (
    <div
      className={`fixed bottom-6 left-1/2 z-[9999] -translate-x-1/2 rounded-xl border bg-white px-5 py-3 shadow-2xl dark:border-slate-700 dark:bg-slate-800 ${className}`}
      style={borderColor ? { borderColor, borderWidth: '2px' } : undefined}
      role="status"
      aria-live="polite"
      data-tour-id="guided-tour-sandbox-banner"
    >
      <div className="flex items-center gap-4">
        <div
          className="h-3 w-3 shrink-0 rounded-full animate-pulse"
          style={{ backgroundColor: borderColor || '#f59e0b' }}
          aria-hidden="true"
        />
        <div className="flex flex-col">
          <span className="font-semibold text-slate-900 dark:text-slate-100">
            {formatSandboxLabel(demoSession?.module_key ?? moduleKey ?? null)} Sandbox Active
          </span>
          <span className="text-sm text-slate-600 dark:text-slate-300">
            Your actions won&apos;t affect real data
          </span>
          {error && (
            <span className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</span>
          )}
        </div>
        <button
          type="button"
          onClick={() => void deactivate()}
          disabled={loading}
          className="ml-2 rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
        >
          {loading ? 'Exiting…' : 'Exit Sandbox'}
        </button>
      </div>
    </div>
  );
}
